import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommandPalette } from '../context/CommandPaletteContext';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import type { ResourceEntry, ResourceEntryKind } from '../utils/resourceIndex';
import { RESOURCE_KIND_LABEL, searchResources } from '../utils/resourceIndex';
import InternalBadge from './InternalBadge';
import ResourceKindIcon from './ResourceKindIcon';

interface Scope {
    label: string;
    /** null means "Everything". */
    kind: ResourceEntryKind | null;
}

const SCOPES: Scope[] = [
    { label: 'Everything', kind: null },
    { label: 'FAQ', kind: 'faq' },
    { label: 'Help Center', kind: 'document' },
    { label: 'Videos', kind: 'video' }
];

const GROUP_ORDER: ResourceEntryKind[] = ['faq', 'document', 'video'];

const GROUP_LABEL: Record<ResourceEntryKind, string> = {
    faq: 'FAQ',
    document: 'Help Center',
    video: 'Videos'
};

/** Enough to scan without scrolling forever; the scope chips narrow further. */
const MAX_RESULTS = 24;

/** Shown before anything is typed, so the palette is never a blank box. */
const RECENT_SUGGESTION_COUNT = 6;

const KEY_HINTS = [
    { key: '↑↓', label: 'navigate' },
    { key: '↵', label: 'open' },
    { key: 'esc', label: 'close' }
];

/** The ⌘K layer: one query across all types, scope chips, keyboard-driven. Mounted once by ResourcesShell. */
export const CommandPalette: React.FC = () => {
    const { isOpen, initialQuery, searchScope, close } = useCommandPalette();
    const { entries } = useResourcesLibrary();
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const [scopeIndex, setScopeIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const activeItemRef = useRef<HTMLButtonElement>(null);

    // Each opening starts from the seed query and the page-declared scope.
    useEffect(() => {
        if (!isOpen) return;
        setQuery(initialQuery);
        setScopeIndex(
            Math.max(
                0,
                SCOPES.findIndex((item) => item.kind === searchScope)
            )
        );
        setActiveIndex(0);
        inputRef.current?.focus();
        inputRef.current?.select();
    }, [isOpen, initialQuery, searchScope]);

    const scope = SCOPES[scopeIndex];

    const results = useMemo(() => {
        const matched = query.trim()
            ? searchResources(entries, query)
            : // No query yet: the most recently published items act as suggestions.
              [...entries].sort((a, b) => b.timestamp - a.timestamp);
        const scoped = scope.kind ? matched.filter((entry) => entry.kind === scope.kind) : matched;
        return scoped.slice(0, query.trim() ? MAX_RESULTS : RECENT_SUGGESTION_COUNT);
    }, [entries, query, scope]);

    const groups = useMemo(() => {
        const byKind = new Map<ResourceEntryKind, ResourceEntry[]>();
        for (const entry of results) {
            const bucket = byKind.get(entry.kind);
            if (bucket) bucket.push(entry);
            else byKind.set(entry.kind, [entry]);
        }
        return GROUP_ORDER.filter((kind) => byKind.has(kind)).map((kind) => ({
            kind,
            label: GROUP_LABEL[kind],
            items: byKind.get(kind) as ResourceEntry[]
        }));
    }, [results]);

    // Groups reorder the flat result list, so highlight order must follow the
    // rendered order rather than the ranked one.
    const flatResults = useMemo(() => groups.flatMap((group) => group.items), [groups]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query, scopeIndex]);

    useEffect(() => {
        activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    if (!isOpen) return null;

    const openEntry = (entry: ResourceEntry) => {
        close();
        navigate(entry.path);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => (flatResults.length ? (index + 1) % flatResults.length : 0));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) =>
                flatResults.length ? (index - 1 + flatResults.length) % flatResults.length : 0
            );
            return;
        }
        if (event.key === 'Tab') {
            event.preventDefault();
            setScopeIndex(
                (index) => (index + (event.shiftKey ? -1 : 1) + SCOPES.length) % SCOPES.length
            );
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const entry = flatResults[activeIndex];
            if (entry) openEntry(entry);
        }
    };

    const hasQuery = query.trim().length > 0;

    return (
        <div
            className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50 pt-[10vh] px-4"
            onClick={close}
            role="presentation"
        >
            <div
                className="w-full max-w-[660px] h-fit max-h-[70vh] bg-white rounded-xl shadow-xl overflow-hidden flex flex-col"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={handleKeyDown}
                role="dialog"
                aria-modal="true"
                aria-label="Search resources"
            >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search everything…"
                        className="flex-1 bg-transparent outline-none text-base text-gray-800 placeholder-gray-400"
                        aria-label="Search resources"
                    />
                    <span className="text-xs font-medium text-gray-400 border border-gray-200 rounded-md px-2 py-1">
                        esc
                    </span>
                </div>

                <div className="flex gap-2 px-5 py-3 border-b border-gray-100 flex-wrap">
                    {SCOPES.map((item, index) => {
                        const active = index === scopeIndex;
                        return (
                            <button
                                key={item.label}
                                type="button"
                                onClick={() => setScopeIndex(index)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-colors ${
                                    active
                                        ? 'border-gray-700 text-gray-800 bg-gray-50'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {item.label}
                            </button>
                        );
                    })}
                </div>

                <div className="overflow-y-auto py-2">
                    {!hasQuery && groups.length > 0 && (
                        <div className="px-5 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-400">
                            Recently added
                        </div>
                    )}

                    {groups.map((group) => (
                        <div key={group.kind} className="py-1">
                            {hasQuery && (
                                <div className="px-5 py-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-400">
                                    {group.label}
                                </div>
                            )}
                            {group.items.map((entry) => {
                                const index = flatResults.indexOf(entry);
                                const active = index === activeIndex;
                                return (
                                    <button
                                        key={entry.key}
                                        ref={active ? activeItemRef : undefined}
                                        type="button"
                                        onClick={() => openEntry(entry)}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                                            active ? 'bg-gray-100' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <ResourceKindIcon kind={entry.kind} />
                                        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                                            <span className="text-sm font-semibold text-gray-800 truncate">
                                                {entry.title}
                                            </span>
                                            {entry.subtitle && (
                                                <span className="text-xs text-gray-500 truncate">
                                                    {entry.subtitle}
                                                </span>
                                            )}
                                        </span>
                                        <InternalBadge audience={entry.audience} />
                                        <span className="text-xs font-medium text-gray-400 flex-shrink-0">
                                            {RESOURCE_KIND_LABEL[entry.kind]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}

                    {hasQuery && groups.length === 0 && (
                        <div className="px-5 py-6 text-center">
                            <span className="text-sm text-gray-500">
                                Nothing matched “{query.trim()}”.
                            </span>
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 px-5 py-3 flex items-center gap-4 bg-gray-50">
                    {KEY_HINTS.map((hint) => (
                        <div key={hint.key} className="flex items-center gap-2">
                            <span className="text-[11px] font-medium text-gray-600 border border-gray-200 bg-white rounded px-1.5 py-0.5">
                                {hint.key}
                            </span>
                            <span className="text-xs text-gray-500">{hint.label}</span>
                        </div>
                    ))}
                    <span className="ml-auto text-xs text-gray-400">
                        {hasQuery
                            ? `${flatResults.length} result${flatResults.length === 1 ? '' : 's'} for “${query.trim()}”`
                            : 'Type to search everything'}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
